import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { evalFractionCalculation, getFixedConveyanceValue } from './ConveyanceUtils'
import * as actions from '@actions'
import { isEqual } from '@/utils'
import ContentEditable from '@/components/contenteditable'

const arePropsEqual = (prev, curr) => {
    if (Object.is(prev, curr)) return true
    
    return isEqual(prev.conveyance, curr.conveyance)
}

const ConveyanceDocCol = memo(({
    x,
    y,
    type,
    documentId,
    party,
    partyType,
    conveyance,
    conveyanceType,
    getLastInteractionRef,
    onInteractionComplete,
}) => {
    
    const [showEdit, setShowEdit] = useState(false)
    const contentEditableRef = useRef()
    
    const handleFractionSave = async val => {
        
        const parsedValue = isNaN(parseFloat(val)) ? '' : parseFloat(val).toString()
        
        await actions.upsertConveyance({
            documentId,
            party,
            type,
            conveyanceType,
            conveyance: {
                newConveyance: !partyType,
                partyType: partyType || (evalFractionCalculation(val) > 0 ? 'grantee' : 'grantor'),
                data: parsedValue,
            },
        })
    }
    
    const wrapInNegative = expression => {
        if (expression.startsWith('=')) {
            return `=-(${expression.slice(1)})`
        }
        
        return `-(${expression})`
    }
    
    const unwrapNegative = expression => {
        if (expression.startsWith('=-(') && expression.endsWith(')')) {
            return `=${expression.slice(3, -1)}`
        }
        
        if (expression.startsWith('-(') && expression.endsWith(')')) {
            return expression.slice(2, -1)
        }
        
        return expression
    }
    
    const isWrappedInNegative = expression =>
        (expression.startsWith('=-(') && expression.endsWith(')')) ||
        (expression.startsWith('-(') && expression.endsWith(')'))
    
    const handleConveyanceValue = (val, partyType) => {
        const valEvaluated = evalFractionCalculation(val)
        
        if (isNaN(valEvaluated)) {
            throw new Error('Invalid expression')
        }
        
        const needsWrapping = (partyType === 'grantor' && valEvaluated > 0) ||
                              (partyType === 'grantee' && valEvaluated < 0)
        
        return needsWrapping ? (isWrappedInNegative(val) ? unwrapNegative(val) : wrapInNegative(val)) : val
    }
    
    const handleCopyValue = useCallback(() => {
        const getDataConveyance = el => el?.getAttribute('data-conveyance')
        
        const handleWithAboveCell = currentTable => {
            const elementAbove = currentTable?.querySelector(`td[data-x="${x-1}"][data-y="${y}"]`)
            const targetContentEditable = elementAbove?.querySelector('div[contenteditable="true"]')
            const valueFromAbove = getDataConveyance(targetContentEditable)
            
            if (valueFromAbove !== null && valueFromAbove !== undefined) {
                contentEditableRef.current?.setEditValue(handleConveyanceValue(valueFromAbove, partyType))
            }
        }
        
        // eslint-disable-next-line no-restricted-globals
        const currentTable = document.activeElement.closest('table')
        const elementOnLeft = currentTable?.querySelector(`td[data-x="${x}"][data-y="${y-1}"]`)
        const targetContentEditable = elementOnLeft?.querySelector('div[contenteditable="true"]')
        const valueFromLeft = getDataConveyance(targetContentEditable)
        
        if (valueFromLeft !== null && valueFromLeft !== undefined) {
            contentEditableRef.current?.setEditValue(handleConveyanceValue(valueFromLeft, partyType))
        } else {
            handleWithAboveCell(currentTable)
        }
    }, [x, y, partyType, contentEditableRef])
    
    const handleOnBlur = (value, element) => {
        element.dataset.conveyance = value
        setShowEdit(false) 
    }
    
    const handleSelectValue = () =>{
        setTimeout(() => {
            const el = contentEditableRef.current?.rootElement?.current?.querySelector('div[contenteditable="true"]')
            
            el?.focus()
            window.getSelection().selectAllChildren(el)
        }, 100)
    }
    
    useEffect(() => {
        if (showEdit && getLastInteractionRef()?.current === 'tab' && !!partyType) {
            handleCopyValue()
            handleSelectValue()
            onInteractionComplete?.()
        }
    }, [showEdit, partyType, getLastInteractionRef, handleCopyValue, onInteractionComplete])
    
    return (
        <td
            className="!p-0 cursor-pointer"
            data-document-id={documentId}
            data-x={x}
            data-y={y}>
            <div
                className="px-2 py-1"
                onFocus={() => { 
                    setShowEdit(true) 
                }}>
                <ContentEditable
                    grow="sm"
                    onBlur={handleOnBlur}
                    alignment="right"
                    value={showEdit ? conveyance.data : getFixedConveyanceValue(conveyance, partyType)}
                    onEditEnd={handleFractionSave}
                    type="textarea" 
                    ref={contentEditableRef} />
            </div>
        </td>
    )
}, arePropsEqual)

ConveyanceDocCol.displayName = 'ConveyanceDocCol'

export default ConveyanceDocCol
